import { CpmInlineErrorBanner } from '../../../components/design-system';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { CONFIG_TEMPLATE_COPY } from '../configTemplateCopy';

interface InfiniteScrollSentinelProps {
    hasMore: boolean;
    loading?: boolean;
    error?: boolean;
    disabled?: boolean;
    onLoadMore: () => void;
    pageSize?: number;
    showEnd?: boolean;
    compact?: boolean;
}

export default function InfiniteScrollSentinel({
    hasMore,
    loading = false,
    error = false,
    disabled = false,
    onLoadMore,
    pageSize = 25,
    showEnd = false,
    compact = false,
}: InfiniteScrollSentinelProps) {
    const sentinelRef = useInfiniteScroll({
        hasMore: hasMore && !error,
        loading,
        disabled,
        onLoadMore,
    });

    const pad = compact ? '8px 0 4px' : '12px 0 6px';

    if (error) {
        return (
            <div style={{ padding: pad }}>
                <CpmInlineErrorBanner
                    compact
                    message={CONFIG_TEMPLATE_COPY.MORE_FAILED}
                    onRetry={onLoadMore}
                    retryLabel={CONFIG_TEMPLATE_COPY.RETRY}
                />
            </div>
        );
    }

    if (loading) {
        const skRows = compact ? 2 : 3;
        const skHeight = compact ? 36 : 48;
        return (
            <div style={{ paddingTop: compact ? 6 : 10 }}>
                <div className="flex items-center" style={{ gap: 9, padding: '6px 0 10px' }}>
                    <span className="cpm-pulse-dot" />
                    <span
                        style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--cpm-text-secondary)' }}
                    >
                        {`Getting the next ${pageSize}…`}
                    </span>
                </div>
                <div className="flex flex-col" style={{ gap: compact ? 8 : 10 }}>
                    {Array.from({ length: skRows }, (_, i) => (
                        <span
                            key={i}
                            className="cpm-sk"
                            style={{ display: 'block', width: '100%', height: skHeight, borderRadius: 10 }}
                        />
                    ))}
                </div>
            </div>
        );
    }

    if (hasMore) {
        return <div ref={sentinelRef} aria-hidden style={{ height: 1, width: '100%' }} />;
    }

    if (!showEnd) return null;

    return (
        <p
            className="m-0 text-center uppercase"
            style={{
                padding: compact ? '8px 0 2px' : 10,
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: '0.08em',
                color: 'var(--cpm-text-tertiary)',
            }}
        >
            {CONFIG_TEMPLATE_COPY.COPY_END_OF_LIST}
        </p>
    );
}
